import axios from 'axios'
import CryptoJS from 'crypto-js'
import {
  getAccessToken,
  removeToken,
  getSelectedStoreId,
  removeSelectedStoreId,
} from './session.manager'
import ApplicationLinks from '../ApplicationLinks'
import { showSubscribePopupAction } from '../store/subscriptionPlan'
import { showPaymentDuePopupAction } from '../store/pendingBills'
import { showTrialExpiredDialogAction } from '../store/trialExpired'
import { showMessageNavigationDialogAction } from '../store/messageNavigationDialog'
import { formatDateWithTimezone } from './utils'
import { AnalyticsManager } from '../analytics'

export const http = axios.create({
  baseURL: process.env.BASE_API_URL,
})

function normalizeNavigationPathFromApiData(data: unknown): string {
  if (typeof data === 'string') return data
  if (data && typeof data === 'object' && 'path' in data) {
    const p = (data as { path?: unknown }).path
    return typeof p === 'string' ? p : ''
  }
  return ''
}

function generateHash(message) {
  const secret =
    'contrastText,backgroundColor:(o.vars||o).palette[e.color].main}))'
  const hash = CryptoJS.HmacSHA256(message, secret).toString(
    CryptoJS.enc.Base64
  )
  return hash
}

export const interceptor = (store) => {
  http.defaults.headers.common['api_key'] = process.env.API_KEY
  http.defaults.headers.common['time-zone'] =
    Intl.DateTimeFormat().resolvedOptions().timeZone
  http.defaults.headers.common['platform'] = 'web'
  http.defaults.headers.common['app-version-code'] = '84'

  http.interceptors.request.use((config) => {
    config.headers['Content-Type'] = 'application/json'

    // Generate current date-time and signature for each request
    const currentDatetime = formatDateWithTimezone(new Date()) //dd-MM-yyyy HH:mm:ss.SSS Z
    const signature = generateHash(currentDatetime)

    config.headers['X-HMAC-Signature'] = signature
    config.headers['Date-Time'] = currentDatetime

    const token = getAccessToken()
    if (token) {
      config.headers['token'] = token
    }
    const store_id = getSelectedStoreId()
    if (store_id) {
      config.headers['store_id'] = store_id
    }
    return config
  })

  http.interceptors.response.use(
    (response) => {
      // Track API errors when response.data.success = false
      if (response.data && response.data.success === false) {
        const endpoint = response.config?.url || 'unknown'
        const apiErrorCode = response.data.code ? String(response.data.code) : String(response.status)
        const apiErrorMessage = response.data.message || response.statusText || 'API request failed'
        AnalyticsManager.shared.trackApiError(endpoint, apiErrorCode, apiErrorMessage)
      }

      if (response.data?.code === 301) {
        window.location.assign(ApplicationLinks.maintenanceLink())
        return Promise.reject(response)
      } else if (response.data.code === 401) {
        removeToken()
        removeSelectedStoreId()
        window.location.assign(ApplicationLinks.loginLink())
        return Promise.reject(response)
      } else if (response.data.code === 406) {
        window.location.assign(ApplicationLinks.onboardingLink())
        return Promise.reject(response)
      } else if (response.data.code === 702) {
        if (process.env.OFFLINE) {
          store.dispatch(showSubscribePopupAction()) // show subscription plan
        } else {
          store.dispatch(showPaymentDuePopupAction()) // show payment due popup
        }
      } else if (response.data.code === 1105) {
        store.dispatch(showTrialExpiredDialogAction())
      } else if (response.data.code === 1103) {
        const path = normalizeNavigationPathFromApiData(response.data.data)
        store.dispatch(
          showMessageNavigationDialogAction({
            message: response.data.message || 'Something went wrong',
            variant: 'error',
            path,
            actionTitle: 'Check Item History',
          })
        )
      }
      return response
    },
    (error) => {
      // Track API errors in error handler
      const endpoint = error.config?.url || 'unknown'
      let errorCode: string | undefined
      let apiErrorMessage: string | undefined

      if (error.response) {
        // HTTP error response received
        const responseCode = error.response.data?.code
        const httpStatus = error.response.status
        
        // Track all API errors including 401/406/702
        errorCode = responseCode ? String(responseCode) : String(httpStatus)
        apiErrorMessage = error.response.data?.message || error.response.statusText || error.message
        AnalyticsManager.shared.trackApiException(endpoint, errorCode, apiErrorMessage)
      } else if (error.request) {
        // Request made but no response received (network error)
        if (!navigator?.onLine) {
          errorCode = 'OFFLINE'
          apiErrorMessage = 'Network is offline'
        } else if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
          errorCode = 'TIMEOUT'
          apiErrorMessage = 'Request timeout'
        } else {
          errorCode = 'NETWORK_ERROR'
          apiErrorMessage = error.message || 'Network error'
        }
        AnalyticsManager.shared.trackApiException(endpoint, errorCode, apiErrorMessage)
      } else {
        // Error in request setup
        errorCode = 'REQUEST_ERROR'
        apiErrorMessage = error.message || 'Request setup error'
        AnalyticsManager.shared.trackApiException(endpoint, errorCode, apiErrorMessage)
      }

      // Handle error and trigger warning message
      const errorMessage = navigator?.onLine
        ? error.message
        : 'Please check your internet connection!!!'
      store.dispatch({
        type: 'ERROR',
        message: errorMessage,
      })
      if (!navigator?.onLine && !process.env.OFFLINE) {
        alert(errorMessage)
      }
      return Promise.reject(error)
    }
  )
}
